import { useState } from "react";
import { PHONE } from "@/mocks/updiag";

interface Props {
  preselectedDiag?: string;
  formTitle?: string;
  formSubtitle?: string;
  ctaLabel?: string;
}

const diagnosticOptions = [
  { value: "dpe", label: "DPE seul" },
  { value: "pack-vente", label: "Pack Vente (DDT complet)" },
  { value: "pack-location", label: "Pack Location" },
  { value: "amiante", label: "Amiante" },
  { value: "plomb", label: "Plomb (CREP)" },
  { value: "electricite", label: "Électricité" },
  { value: "gaz", label: "Gaz" },
  { value: "termites", label: "Termites" },
  { value: "loi-carrez", label: "Loi Carrez" },
];

export default function LpForm({ preselectedDiag = "dpe", formTitle = "Devis gratuit en 2 min", formSubtitle = "Réponse sous 2h — Sans engagement", ctaLabel = "Obtenir mon devis gratuit" }: Props) {
  const [formData, setFormData] = useState({
    diagnostic: preselectedDiag,
    type: "appartement",
    surface: "",
    ville: "",
    nom: "",
    email: "",
    telephone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch("https://readdy.ai/api/form/d7evktuivmjfhtdrfkmg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-checkbox-circle-fill text-[#16A34A] text-3xl"></i>
        </div>
        <h3 className="text-xl font-extrabold text-[#0F3B5F] mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>
          Demande envoyée !
        </h3>
        <p className="text-gray-500 text-sm mb-4">Notre équipe vous contacte sous <strong>2h</strong> avec votre devis personnalisé.</p>
        <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 px-6 py-3 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap text-sm">
          <i className="ri-phone-fill"></i> Appeler maintenant
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-7">
      <h3 className="text-xl font-extrabold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>{formTitle}</h3>
      <p className="text-sm text-gray-500 mb-6">{formSubtitle}</p>

      <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Diagnostic souhaité</label>
          <select name="diagnostic" value={formData.diagnostic} onChange={(e) => setFormData({ ...formData, diagnostic: e.target.value })}
            className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors">
            {diagnosticOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Type de bien</label>
            <select name="type" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors">
              <option value="appartement">Appartement</option>
              <option value="maison">Maison</option>
              <option value="local">Local commercial</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Surface (m²)</label>
            <input type="number" name="surface" placeholder="Ex: 65" value={formData.surface}
              onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Ville / Arrondissement</label>
          <input type="text" name="ville" placeholder="Ex: Paris 15e, Boulogne-Billancourt..." value={formData.ville}
            onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
            className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" />
        </div>

        <div className="border-t border-gray-100 pt-4">
          <div>
            <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Nom complet *</label>
            <input type="text" name="nom" placeholder="Jean Dupont" value={formData.nom}
              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" required />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Email *</label>
          <input type="email" name="email" placeholder="jean@exemple.fr" value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" required />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Téléphone</label>
          <input type="tel" name="telephone" placeholder="06 XX XX XX XX" value={formData.telephone}
            onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
            className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" />
        </div>

        <button type="submit" disabled={submitting || !formData.nom || !formData.email}
          className="w-full py-4 bg-[#F97316] text-white rounded-xl font-extrabold text-base hover:bg-orange-600 transition-all disabled:opacity-50 whitespace-nowrap">
          {submitting ? "Envoi en cours..." : ctaLabel}
          {!submitting && <i className="ri-arrow-right-line ml-2"></i>}
        </button>

        <p className="text-[11px] text-gray-400 text-center">100% gratuit — Sans engagement — Réponse sous 2h</p>
      </form>
    </div>
  );
}
